import { NgClass } from '@angular/common';
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EffectRef,
    ElementRef,
    HostBinding,
    OnDestroy,
    OnInit,
    effect,
    inject,
    input,
    output,
    viewChild,
} from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MomentDateAdapter } from '@angular/material-moment-adapter';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatTimepickerModule, MatTimepickerSelected } from '@angular/material/timepicker';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import moment from 'moment';
import { Subject, takeUntil } from 'rxjs';
import { CultureService } from '../../services/culture.service';
import { setClassPanelAgGrid } from '../se-helper-pickers';
import { SE_UI_DATE_TIME_FORMATS } from '../se-ui-date-formats';

@Component({
    selector: 'se-time-picker',
    templateUrl: './se-time-picker.component.html',
    styleUrls: ['./se-time-picker.component.scss'],
    providers: [
        { provide: MAT_DATE_FORMATS, useValue: SE_UI_DATE_TIME_FORMATS },
        {
            provide: DateAdapter,
            useClass: MomentDateAdapter,
            deps: [MAT_DATE_LOCALE],
        },
    ],
    imports: [
        MatTimepickerModule,
        FormsModule,
        MatFormFieldModule,
        MatIconModule,
        MatInputModule,
        ReactiveFormsModule,
        TranslateModule,
        NgClass,
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SeTimePickerComponent implements OnInit, OnDestroy {
    @HostBinding('style.display')
    display = 'flex';

    timePicker = viewChild<ElementRef>('seTimePickerInput');

    //private time: TimePart | null = null;
    private _onDestroy = new Subject<void>();
    isOnError: boolean = false;
    timeControl: FormControl<moment.Moment | null> = new FormControl(null);
    timeFormats: string = '';
    private timeEffect: EffectRef;

    //inject
    private readonly translateService = inject(TranslateService);
    private readonly culture = inject(CultureService);

    //input
    appearance = input<string>('outline');
    clockAmPm = input<number>(0);
    customPanelClass = input<string>('');
    disabled = input<boolean>(false);
    gridClass = input<string>('');
    highlightBackgroundEnabled = input<boolean>(false);
    hourPlaceholder = input<string>('hh');
    isWithLabel = input<boolean>(true);
    label = input<string>(this.translateService.instant('Time'));
    minutePlaceholder = input<string>('mm');
    required = input<boolean>(false);
    setError = input<boolean>();
    showIcon = input<boolean>(true);
    showResetButton = input<boolean>(true);
    time = input<moment.Moment | null>();
    timeFormat = input<string>('HH:mm');

    //output
    timeChange = output<moment.Moment | null>();
    errorStateChange = output<boolean>();

    constructor(private changeDetectionRef: ChangeDetectorRef) {
        this.timeEffect = effect(() => {
            const date = this.time() ?? null;
            if (this.timeControl.value !== date) {
                this.timeControl.setValue(date, { emitEvent: false });
            }

            if (this.disabled() !== this.timeControl.disabled) {
                this.disabled()
                    ? this.timeControl.disable({ emitEvent: false })
                    : this.timeControl.enable({ emitEvent: false });
            }

            const validators = this.required() ? [Validators.required] : [];
            this.timeControl.setValidators(validators);
            this.timeControl.updateValueAndValidity({ emitEvent: false });
        });
    }

    ngOnInit(): void {
        this.initFormControl();

        if (this.culture.getCulture() == 'en-US' || (this.clockAmPm() && this.clockAmPm() === 12)) {
            this.setFormat();
        }

        this.timeControl.valueChanges.pipe(takeUntil(this._onDestroy)).subscribe({
            next: (value) => {
                this.timeChange.emit(value);
            },
        });

        this.timeControl.statusChanges.pipe(takeUntil(this._onDestroy)).subscribe({
            next: (status) => {
                if (this.timeControl.errors || this.setError()) {
                    this.isOnError = true;
                    this.errorStateChange.emit(true);
                } else {
                    this.isOnError = false;
                }
            },
        });
    }

    private initFormControl() {
        const timeDisplayed = this.time() ?? null;
        if (!this.required()) {
            this.timeControl = new FormControl<moment.Moment | null>(
                { value: timeDisplayed, disabled: this.disabled() },
                { updateOn: 'blur' },
            );
        } else {
            this.timeControl = new FormControl<moment.Moment | null>(
                { value: timeDisplayed, disabled: this.disabled() },
                { validators: Validators.required, updateOn: 'blur' },
            );
        }
        this.timeChange.emit(timeDisplayed);
    }

    ngOnDestroy() {
        this.timeEffect.destroy();
        this._onDestroy.unsubscribe();
    }

    private setFormat() {
        //format 12 di default è 24
        SE_UI_DATE_TIME_FORMATS.display.timeOptionLabel = 'h:mm A';
        this.timeFormats = 'h:mm A';
    }

    onTimeChanging(eventTime: MatTimepickerSelected<moment.Moment>) {
        if (eventTime) {
            this.timeControl.patchValue(eventTime.value);
            this.changeDetectionRef.detectChanges();
        }
    }

    getErrorMessage(): string {
        return this.required() && this.isOnError
            ? this.translateService.instant('MandatoryData')
            : this.translateService.instant('Error');
    }

    // Funzioni di manipolazione dall'esterno

    focus() {
        const tipePicker = this.timePicker();
        tipePicker?.nativeElement.focus();
    }

    select() {
        const tipePicker = this.timePicker();
        tipePicker?.nativeElement.select();
    }

    onOpenTime() {
        if (this.customPanelClass()) {
            //al momento il timepicker non ha un panel class
            setClassPanelAgGrid(this.customPanelClass());
        }
    }
}
